import React, { useEffect, useRef, useState } from "react";
import { Sparkle, PaperPlaneTilt } from "@phosphor-icons/react";

/**
 * HeroAssistantMockup — anteprima della chat con l'assistente Pronto.
 *
 * Animazione: typewriter sulla domanda agente, pausa, typewriter sulla
 * risposta Pronto, loop. Tutto in 12 secondi.
 */
const CONVO = [
  {
    role: "user",
    text: "Cliente vende casa ricevuta in donazione 7 anni fa. La banca dell'acquirente blocca il mutuo. Cosa propongo?",
  },
  {
    role: "assistant",
    text: "Caso classico. Hai 3 strade: 1) polizza decennale anti-donazione (~0,8% del prezzo, paga il venditore), accettata da quasi tutte le banche. 2) Atto di rinuncia all'azione di riduzione dei legittimari, ma servono tutti i fratelli/genitori vivi. 3) Aspettare 10 anni dalla morte del donante. La 1 è la più rapida: ti consiglio di contattare subito un broker assicurativo (Lloyd's, AXA, Generali la fanno).",
  },
];

const CHAR_PER_TICK = 2;
const TICK_MS = 35;
const PAUSE_BETWEEN_MS = 1800;
const PAUSE_AT_END_MS = 3500;

export default function HeroAssistantMockup() {
  const [visible, setVisible] = useState([{ role: "user", text: "" }]);
  const [typing, setTyping] = useState(true);

  useEffect(() => {
    let mounted = true;
    let timer;

    const run = async () => {
      while (mounted) {
        // Reset
        setVisible([{ role: "user", text: "" }]);
        setTyping(true);

        // Type user message
        for (let i = 0; i <= CONVO[0].text.length; i += CHAR_PER_TICK) {
          if (!mounted) return;
          setVisible((v) => {
            const copy = [...v];
            copy[0] = { role: "user", text: CONVO[0].text.slice(0, i) };
            return copy;
          });
          await sleep(TICK_MS);
        }

        // Pause between
        await sleep(PAUSE_BETWEEN_MS);
        if (!mounted) return;

        // Add empty assistant message + dots
        setVisible((v) => [...v, { role: "assistant", text: "" }]);

        // Wait for "thinking" dots
        await sleep(900);
        if (!mounted) return;

        // Type assistant message
        for (let i = 0; i <= CONVO[1].text.length; i += CHAR_PER_TICK) {
          if (!mounted) return;
          setVisible((v) => {
            const copy = [...v];
            copy[1] = { role: "assistant", text: CONVO[1].text.slice(0, i) };
            return copy;
          });
          await sleep(TICK_MS);
        }

        setTyping(false);
        await sleep(PAUSE_AT_END_MS);
      }
    };
    run();

    return () => { mounted = false; if (timer) clearTimeout(timer); };
  }, []);

  return (
    <div className="max-w-[440px] mx-auto" data-testid="hero-assistant-mockup">
      <div className="relative bg-white rounded-2xl border border-[#E5E7E0] shadow-lg overflow-hidden" style={{ minHeight: 320 }}>
        {/* Header */}
        <div className="px-4 py-3 border-b border-[#EEEBE3] flex items-center gap-2.5 bg-gradient-to-r from-[#F1F4ED] to-white">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#7C8B6E] to-[#A8B89A] flex items-center justify-center">
            <Sparkle size={14} weight="fill" className="text-white" />
          </div>
          <div>
            <div className="text-[12.5px] font-semibold text-[#1C1C1A] leading-tight">Pronto</div>
            <div className="text-[10px] text-[#7C8B6E]">Il tuo consulente immobiliare</div>
          </div>
        </div>

        {/* Messages */}
        <div className="px-4 py-4 space-y-3 min-h-[200px]">
          {visible.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"} gap-2`}>
              {m.role === "assistant" && (
                <div className="w-6 h-6 rounded-full bg-gradient-to-br from-[#7C8B6E] to-[#A8B89A] flex items-center justify-center shrink-0 mt-0.5">
                  <Sparkle size={10} weight="fill" className="text-white" />
                </div>
              )}
              <div
                className={`max-w-[78%] px-3 py-2 rounded-2xl text-[11.5px] leading-[1.5] whitespace-pre-wrap break-words ${
                  m.role === "user"
                    ? "bg-[#1C1C1A] text-white rounded-tr-md"
                    : "bg-[#F4F2EC] text-[#1C1C1A] rounded-tl-md"
                }`}
              >
                {m.text || (m.role === "assistant" ? <TypingDots /> : "\u00A0")}
                {/* cursor lampeggiante */}
                {((m.role === "user" && m.text.length < CONVO[0].text.length) ||
                  (m.role === "assistant" && m.text && m.text.length < CONVO[1].text.length)) && (
                  <span className="inline-block w-[2px] h-[10px] bg-current align-middle ml-0.5 animate-pulse" />
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Mock input bar */}
        <div className="px-4 py-3 border-t border-[#EEEBE3] bg-[#FAF9F5] flex items-center gap-2">
          <div className="flex-1 px-3 py-1.5 bg-white border border-[#E5E7E0] rounded-full text-[10.5px] text-[#9A968B]">
            Chiedi qualcosa…
          </div>
          <div className="w-8 h-8 rounded-full bg-[#7C8B6E] flex items-center justify-center shrink-0">
            <PaperPlaneTilt size={12} weight="fill" className="text-white" />
          </div>
        </div>
      </div>
    </div>
  );
}

function TypingDots() {
  return (
    <span className="inline-flex items-center gap-1 py-0.5">
      <span className="w-1 h-1 rounded-full bg-[#7C8B6E] animate-bounce" style={{ animationDelay: "0ms" }} />
      <span className="w-1 h-1 rounded-full bg-[#7C8B6E] animate-bounce" style={{ animationDelay: "150ms" }} />
      <span className="w-1 h-1 rounded-full bg-[#7C8B6E] animate-bounce" style={{ animationDelay: "300ms" }} />
    </span>
  );
}

function sleep(ms) {
  return new Promise((res) => setTimeout(res, ms));
}
